<template>
  <button
    :class="[
      'c-button',
      `c-button--${variant}`,
      color && `c-button--color-${color}`,
      `c-button--radius-${radius}`,
      { 'c-button--loading': loading },
      { 'c-button--block': block },
      { 'c-button--rounded': rounded },
      { 'c-button--icon-only': iconOnly || !label },
      { 'c-button--sm': size === 'small' },
      { 'c-button--lg': size === 'large' },
    ]"
    :disabled="disabled || loading"
    :aria-busy="loading"
    :aria-disabled="disabled || loading"
    :type="type"
    @click="handleClick"
    @keydown.enter="$emit('click', $event)"
    @keydown.space="$emit('click', $event)"
  >
    <div class="c-button__content" v-if="!loading">
      <i v-if="icon && (iconPos ? iconPos === 'left' : iconPosition === 'left')" :class="[icon, {'c-button__icon--left': !iconOnly && label}]" class="c-button__icon" aria-hidden="true"></i>
      <span v-if="!iconOnly && label" class="c-button__label">{{ label }}</span>
      <i v-if="icon && (iconPos ? iconPos === 'right' : iconPosition === 'right')" :class="[icon, {'c-button__icon--right': !iconOnly && label}]" class="c-button__icon" aria-hidden="true"></i>
    </div>
    <div v-else class="c-button__loading-container" role="status">
      <span class="sr-only">Loading...</span>
      <div class="c-button__spinner"></div>
    </div>
    <span v-if="ripple" :class="['c-button__ripple', getRippleClass()]" ref="ripple"></span>
  </button>
</template>

<script>
// Using JavaScript instead of TypeScript for better compatibility
export default {
  name: "CButton",
  props: {
    label: {
      type: String,
      default: ""
    },
    variant: {
      type: String,
      default: "solid",
      validator: (value) => [
        "text", "outlined", "solid", "faded", "bordered", "light", "flat", "ghost", "shadow"
      ].includes(value)
    },
    color: {
      type: String,
      default: null,
      validator: (value) => 
        !value || ["default", "primary", "secondary", "success", "danger", "warning", "info"].includes(value)
    },
    icon: {
      type: String,
      default: null
    },
    iconPosition: {
      type: String,
      default: "left",
      validator: (value) => ["left", "right"].includes(value)
    },
    iconPos: {
      type: String,
      default: null,
      validator: (value) => !value || ["left", "right"].includes(value)
    },
    iconOnly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    block: {
      type: Boolean,
      default: false
    },
    rounded: {
      type: Boolean,
      default: false
    },
    ripple: {
      type: Boolean,
      default: true
    },
    radius: {
      type: String,
      default: "sm",
      validator: (value) => ["none", "sm", "md", "lg", "full"].includes(value)
    },
    size: {
      type: String,
      default: "default",
      validator: (value) => ["small", "default", "large"].includes(value)
    },
    type: {
      type: String,
      default: "button",
      validator: (value) => ["button", "submit", "reset"].includes(value)
    }
  },
  data() {
    return {
      isRippling: false
    };
  },
  emits: ['click'],
  mounted() {
    // Ensure the ripple container is ready
    this.$nextTick(() => {
      if (this.$refs.ripple) {
        this.$refs.ripple.style.width = '0px';
        this.$refs.ripple.style.height = '0px';
      }
    });
  },
  methods: {
    handleClick(e) {
      if (this.disabled || this.loading) return;
      
      // Only create ripple effect if ripple prop is true
      if (this.ripple) {
        try {
          this.isRippling = true;
          
          // Create ripple effect
          const btn = e.currentTarget;
          const ripple = this.$refs.ripple;
          
          if (!ripple) {
            this.$emit('click', e);
            return;
          }
          
          const rect = btn.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          
          const size = Math.max(rect.width, rect.height) * 2.5;
          
          // Reset ripple
          ripple.style.width = '0px';
          ripple.style.height = '0px';
          ripple.style.left = '0px';
          ripple.style.top = '0px';
          ripple.classList.remove('animate');
          
          // Force repaint
          void ripple.offsetWidth;
          
          // Set new ripple position and size
          ripple.style.left = `${x}px`;
          ripple.style.top = `${y}px`;
          ripple.style.width = `${size}px`;
          ripple.style.height = `${size}px`;
          
          // Trigger animation
          ripple.classList.add('animate');
          
          // Reset rippling state after animation completes
          setTimeout(() => {
            this.isRippling = false;
          }, 1500);
        } catch (error) {
          console.error('Ripple effect error:', error);
          this.isRippling = false;
        }
      }
      
      this.$emit('click', e);
    },
    
    // Return the appropriate ripple class based on variant and color
    getRippleClass() {
      // For light variants use dark ripple
      if (['text', 'ghost', 'light', 'bordered', 'faded', 'outlined'].includes(this.variant)) {
        return 'c-button__ripple--dark';
      }
      // For dark variants use light ripple
      return 'c-button__ripple--light';
    }
  }
};
</script>

<style lang="scss">
/* Global CSS variables */
:root {
  --button-radius-none: 0;
  --button-radius-sm: 8px;
  --button-radius-md: 12px;
  --button-radius-lg: 14px;
  --button-radius-full: 9999px;
}
</style>

<style lang="scss" scoped>
// Define variables with CSS custom properties for better theming support
:root {
  --button-font-family: "Jost", sans-serif;
  --button-text-base: 16px;
  --button-text-xs: 12px;
  --button-text-md: 18px;
  --button-font-weight: 500;
  
  /* Modern color palette */
  --button-primary: #015cac;
  --button-primary-hover: #014a8a;
  --button-primary-active: #013e75;
  --button-primary-rgb: 1, 92, 172;
  
  --button-secondary: #fc6c2a;
  --button-secondary-hover: #e25a1c;
  --button-secondary-active: #c94c11;
  --button-secondary-rgb: 252, 108, 42;
  
  --button-text-color: #475569;
  --button-white: #ffffff;
  
  --button-success: #16a34a;
  --button-success-hover: #109040;
  --button-success-active: #0e7a36;
  --button-success-rgb: 22, 163, 74;
  
  --button-danger: #e11d48;
  --button-danger-hover: #c01941;
  --button-danger-active: #a01538;
  --button-danger-rgb: 225, 29, 72;
  
  --button-warning: #f59e0b;
  --button-warning-hover: #d97706;
  --button-warning-active: #b45309;
  --button-warning-rgb: 245, 158, 11;
  
  --button-info: #0ea5e9;
  --button-info-hover: #0284c7;
  --button-info-active: #0369a1;
  --button-info-rgb: 14, 165, 233;
  
  --button-border-radius: 12px;
  --button-border-radius-rounded: 50px;
  --button-transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  --button-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  --button-shadow-hover: 0 4px 8px rgba(0, 0, 0, 0.1);
  --button-padding-x: 16px;
  --button-padding-y: 8px;
}

// For older browsers fallback
$font-primary: "Jost", sans-serif;
$text-base: 16px;
$text-xs: 12px;
$text-md: 18px;
$font-base: 500;
$brand-color: #015cac;
$text-secondary: #fc6c2a;
$text-primary: #475569;
$white: #fff;

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.c-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: var(--button-font-family, $font-primary);
  font-size: var(--button-text-base, $text-base);
  font-weight: var(--button-font-weight, $font-base);
  padding: var(--button-padding-y, 8px) var(--button-padding-x, 16px);
  cursor: pointer;
  transition: var(--button-transition, all 0.3s ease);
  border: none;
  outline:2px solid transparent;
  outline-offset: 2px;
  position: relative;
  text-align: center;
  box-shadow: var(--button-shadow);
  overflow: hidden;

  /* Default border-radius - will be overridden by radius classes */
  border-radius: 12px;
  
  &--radius-none {
    border-radius: 0 !important;
  }
  
  &--radius-sm {
    border-radius: 8px !important;
  }
  
  &--radius-md {
    border-radius: 12px !important;
  }
  
  &--radius-lg {
    border-radius: 14px !important;
  }
  
  &--radius-full {
    border-radius: 9999px !important;
  }
  
  /* Ripple effect */
  &__ripple {
    position: absolute;
    top: 0;
    left: 0;
    border-radius: 50%;
    transform: translate(-50%, -50%) scale(0);
    pointer-events: none;
    z-index: 0;
    will-change: transform, opacity;
    
    &.animate {
      animation: ripple 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    }
    
    /* Light ripple for dark backgrounds */
    &--light {
      background-color: rgba(255, 255, 255, 0.5);
    }
    
    /* Dark ripple for light backgrounds */
    &--dark {
      background-color: rgba(0, 0, 0, 0.15);
    }
  }
  
  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
  
  &__content {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 1;
  }
  
  &__icon {
    display: inline-flex;
    align-items: center;
    
    &--left {
      margin-right: 8px;
    }
    
    &--right {
      margin-left: 8px;
    }
  }
  
  &__loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 1;
  }
  
  &__spinner {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: #fff;
    animation: spinner 0.8s linear infinite;
  }
  
  &--primary {
    background-color: var(--button-primary, $brand-color);
    color: var(--button-white, $white);
    
    &:hover:not(:disabled) {
      background-color: var(--button-primary-hover, darken($brand-color, 10%));
      box-shadow: var(--button-shadow-hover);
    }
    
    &:active:not(:disabled) {
      background-color: var(--button-primary-active, darken($brand-color, 15%));
      box-shadow: var(--button-shadow);
    }
  }
  
  &--secondary {
    background-color: var(--button-secondary, $text-secondary);
    color: var(--button-white, $white);
    
    &:hover:not(:disabled) {
      background-color: var(--button-secondary-hover, darken($text-secondary, 10%));
      box-shadow: var(--button-shadow-hover);
    }
    
    &:active:not(:disabled) {
      background-color: var(--button-secondary-active, darken($text-secondary, 15%));
      box-shadow: var(--button-shadow);
    }
  }
  
  &--success {
    background-color: var(--button-success, #16a34a);
    color: var(--button-white, $white);
    
    &:hover:not(:disabled) {
      background-color: var(--button-success-hover, darken(#16a34a, 10%));
      box-shadow: var(--button-shadow-hover);
    }
    
    &:active:not(:disabled) {
      background-color: var(--button-success-active, darken(#16a34a, 15%));
      box-shadow: var(--button-shadow);
    }
  }
  
  &--danger {
    background-color: var(--button-danger, #e11d48);
    color: var(--button-white, $white);
    
    &:hover:not(:disabled) {
      background-color: var(--button-danger-hover, darken(#e11d48, 10%));
      box-shadow: var(--button-shadow-hover);
    }
    
    &:active:not(:disabled) {
      background-color: var(--button-danger-active, darken(#e11d48, 15%));
      box-shadow: var(--button-shadow);
    }
  }
  
  &--warning {
    background-color: var(--button-warning, #f59e0b);
    color: var(--button-text-color, $text-primary);
    
    &:hover:not(:disabled) {
      background-color: var(--button-warning-hover, darken(#f59e0b, 10%));
      box-shadow: var(--button-shadow-hover);
    }
    
    &:active:not(:disabled) {
      background-color: var(--button-warning-active, darken(#f59e0b, 15%));
      box-shadow: var(--button-shadow);
    }
  }
  
  &--info {
    background-color: var(--button-info, #0ea5e9);
    color: var(--button-white, $white);
    
    &:hover:not(:disabled) {
      background-color: var(--button-info-hover, darken(#0ea5e9, 10%));
      box-shadow: var(--button-shadow-hover);
    }
    
    &:active:not(:disabled) {
      background-color: var(--button-info-active, darken(#0ea5e9, 15%));
      box-shadow: var(--button-shadow);
    }
  }
  
  &--text {
    background-color: transparent;
    color: var(--button-primary, $brand-color);
    box-shadow: none;
    
    &:hover:not(:disabled) {
      background-color: rgba(var(--button-primary-rgb, 1, 92, 172), 0.08);
      text-decoration: underline;
    }
    
    &:active:not(:disabled) {
      background-color: rgba(var(--button-primary-rgb, 1, 92, 172), 0.12);
    }
  }
  
  &--outlined {
    background-color: transparent;
    color: var(--button-primary, $brand-color);
    border: 1px solid var(--button-primary, $brand-color);
    box-shadow: none;
    
    &:hover:not(:disabled) {
      background-color: rgba(var(--button-primary-rgb, 1, 92, 172), 0.08);
      box-shadow: var(--button-shadow);
    }
    
    &:active:not(:disabled) {
      background-color: rgba(var(--button-primary-rgb, 1, 92, 172), 0.12);
      box-shadow: none;
    }
  }
  
  &--solid {
    background-color: var(--button-primary, $brand-color);
    color: var(--button-white, $white);
    border: none;
    
    &:hover:not(:disabled) {
      background-color: var(--button-primary-hover, darken($brand-color, 10%));
    }
    
    &:active:not(:disabled) {
      background-color: var(--button-primary-active, darken($brand-color, 15%));
    }
  }
  
  &--faded {
    background-color: rgba(var(--button-primary-rgb, 1, 92, 172), 0.15);
    color: var(--button-primary, $brand-color);
    border: none;
    box-shadow: none;
    
    &:hover:not(:disabled) {
      background-color: rgba(var(--button-primary-rgb, 1, 92, 172), 0.25);
    }
    
    &:active:not(:disabled) {
      background-color: rgba(var(--button-primary-rgb, 1, 92, 172), 0.35);
    }
  }
  
  &--bordered {
    background-color: transparent;
    color: var(--button-primary, $brand-color);
    border: 2px solid var(--button-primary, $brand-color);
    box-shadow: none;
    
    &:hover:not(:disabled) {
      background-color: rgba(var(--button-primary-rgb, 1, 92, 172), 0.08);
    }
    
    &:active:not(:disabled) {
      background-color: rgba(var(--button-primary-rgb, 1, 92, 172), 0.12);
    }
  }
  
  &--light {
    background-color: rgba(var(--button-primary-rgb, 1, 92, 172), 0.1);
    color: var(--button-primary, $brand-color);
    border: none;
    box-shadow: none;
    
    &:hover:not(:disabled) {
      background-color: rgba(var(--button-primary-rgb, 1, 92, 172), 0.2);
    }
    
    &:active:not(:disabled) {
      background-color: rgba(var(--button-primary-rgb, 1, 92, 172), 0.3);
    }
  }
  
  &--flat {
    background-color: var(--button-primary, $brand-color);
    color: var(--button-white, $white);
    border: none;
    box-shadow: none;
    border-radius: 2px;
    
    &:hover:not(:disabled) {
      background-color: var(--button-primary-hover, darken($brand-color, 10%));
    }
    
    &:active:not(:disabled) {
      background-color: var(--button-primary-active, darken($brand-color, 15%));
    }
  }
  
  &--ghost {
    background-color: transparent;
    color: var(--button-primary, $brand-color);
    border: 1px solid transparent;
    box-shadow: none;
    
    &:hover:not(:disabled) {
      background-color: rgba(var(--button-primary-rgb, 1, 92, 172), 0.08);
      border-color: var(--button-primary, $brand-color);
    }
    
    &:active:not(:disabled) {
      background-color: rgba(var(--button-primary-rgb, 1, 92, 172), 0.12);
    }
  }
  
  &--shadow {
    background-color: var(--button-primary, $brand-color);
    color: var(--button-white, $white);
    border: none;
    box-shadow: 0 4px 8px rgba(var(--button-primary-rgb, 1, 92, 172), 0.3);
    
    &:hover:not(:disabled) {
      background-color: var(--button-primary-hover, darken($brand-color, 10%));
      box-shadow: 0 6px 12px rgba(var(--button-primary-rgb, 1, 92, 172), 0.4);
    }
    
    &:active:not(:disabled) {
      background-color: var(--button-primary-active, darken($brand-color, 15%));
      box-shadow: 0 2px 4px rgba(var(--button-primary-rgb, 1, 92, 172), 0.2);
    }
  }
  
  &--block {
    display: flex;
    width: 100%;
  }
  
  // Color variants
  &--color-default {
    --button-primary: #64748b;
    --button-primary-hover: #475569;
    --button-primary-active: #334155;
    --button-primary-rgb: 100, 116, 139;
  }
  
  &--color-primary {
    --button-primary: #015cac;
    --button-primary-hover: #014a8a;
    --button-primary-active: #013e75;
    --button-primary-rgb: 1, 92, 172;
  }
  
  &--color-secondary {
    --button-primary: #fc6c2a;
    --button-primary-hover: #e25a1c;
    --button-primary-active: #c94c11;
    --button-primary-rgb: 252, 108, 42;
  }
  
  &--color-success {
    --button-primary: #16a34a;
    --button-primary-hover: #109040;
    --button-primary-active: #0e7a36;
    --button-primary-rgb: 22, 163, 74;
  }
  
  &--color-danger {
    --button-primary: #e11d48;
    --button-primary-hover: #c01941;
    --button-primary-active: #a01538;
    --button-primary-rgb: 225, 29, 72;
  }
  
  &--color-warning {
    --button-primary: #f59e0b;
    --button-primary-hover: #d97706;
    --button-primary-active: #b45309;
    --button-primary-rgb: 245, 158, 11;
  }
  
  &--color-info {
    --button-primary: #0ea5e9;
    --button-primary-hover: #0284c7;
    --button-primary-active: #0369a1;
    --button-primary-rgb: 14, 165, 233;
  }
  
  &--loading {
    cursor: wait;
  }
  
  &--rounded {
    border-radius: var(--button-border-radius-rounded, 50px);
  }
  
  &--icon-only {
    padding: var(--button-padding-y, 8px);
    aspect-ratio: 1 / 1;
    
    .c-button__icon {
      margin: 0;
    }
  }
  
  &--sm {
    font-size: var(--button-text-xs, $text-xs);
    padding: 6px 12px;
    
    &.c-button--icon-only {
      padding: 6px;
    }
    
    .c-button__spinner {
      width: 12px;
      height: 12px;
    }
  }
  
  &--lg {
    font-size: var(--button-text-md, $text-md);
    padding: 12px 24px;
    
    &.c-button--icon-only {
      padding: 12px;
    }
    
    .c-button__spinner {
      width: 20px;
      height: 20px;
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none;
  }
}

@keyframes spinner {
  to {
    transform: rotate(360deg);
  }
}

@keyframes ripple {
  0% {
    transform: translate(-50%, -50%) scale(0);
    opacity: 0.6;
  }
  100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .c-button {
    transition: none;
    
    &__spinner {
      animation-duration: 1.5s;
    }
    
    &__ripple {
      animation: none;
    }
  }
}

@media (hover: hover) {
  .c-button:hover:not(:disabled) {
    /* No transform */
  }
}
</style> 